import classNames from 'classnames';
import {
  addMonths,
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import { useEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';
import { createPortal } from 'react-dom';

import { capitalize, formatDateRu } from '../../utils';
import { BottomSheet } from './BottomSheet';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

/** Стрелка «‹» из календаря пациента — чтобы пикеры выглядели одинаково. */
const ArrowIcon = () => (
  <svg width="6" height="12" viewBox="0 0 6 12" fill="none">
    <path
      d="M5.45215 1.28014C5.74504 0.987243 5.74504 0.512483 5.45215 0.219589C5.15924 -0.0731421 4.68444 -0.0732498 4.3916 0.219589L1.6543 2.95689C1.26706 3.34413 0.944099 3.6662 0.703125 3.95006C0.456544 4.24054 0.251713 4.54038 0.134766 4.90025C-0.0445833 5.45238 -0.0445433 6.04733 0.134766 6.59947C0.251686 6.95931 0.456591 7.25921 0.703125 7.54967C0.944069 7.83349 1.26712 8.15566 1.6543 8.54283L4.3916 11.2801C4.68449 11.573 5.15925 11.573 5.45215 11.2801C5.74504 10.9872 5.74504 10.5125 5.45215 10.2196L2.71484 7.48228C2.31019 7.07763 2.03877 6.80524 1.84668 6.57896C1.66071 6.35985 1.59298 6.23315 1.56152 6.13658C1.48001 5.88558 1.47997 5.61414 1.56152 5.36314C1.593 5.26656 1.66065 5.13992 1.84668 4.92076C2.03879 4.69446 2.31011 4.42218 2.71484 4.01744L5.45215 1.28014Z"
      fill="#151515"
    />
  </svg>
);

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Выбранная дата — с неё открывается календарь. */
  value?: Date;
  onSelect: (date: Date) => void;
  /** Поле, под которым разворачивается календарь на десктопе. */
  anchorRef?: RefObject<HTMLElement>;
  title?: string;
};

/**
 * Выбор даты сеткой месяца: на десктопе — карточка под полем, на мобилке —
 * шторка снизу. Оформление общее с выбором месяца в календаре пациента.
 */
export const DatePickerModal = ({
  isOpen,
  onClose,
  value,
  onSelect,
  anchorRef,
  title = 'Дата начала',
}: Props) => {
  const [viewDate, setViewDate] = useState(() => value ?? new Date());
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? true
      : window.matchMedia('(min-width: 768px)').matches,
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(min-width: 768px)');
    const handler = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    setIsDesktop(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Открываем на месяце выбранной даты, а не там, где остановились в прошлый раз.
  useEffect(() => {
    if (isOpen) setViewDate(value ?? new Date());
  }, [isOpen, value]);

  const weeks = useMemo(() => {
    const first = startOfWeek(startOfMonth(viewDate), { weekStartsOn: 1 });
    const last = endOfWeek(endOfMonth(viewDate), { weekStartsOn: 1 });
    const result: Date[][] = [];
    for (let day = first; day <= last; ) {
      const week: Date[] = [];
      for (let i = 0; i < 7; i++) {
        week.push(day);
        day = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
      }
      result.push(week);
    }
    return result;
  }, [viewDate]);

  const today = new Date();

  const grid = (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full items-center justify-between">
        <button
          type="button"
          onClick={() => setViewDate(addMonths(viewDate, -1))}
          className="flex items-center gap-2.5 rounded-[100px] bg-[#f1f7fa] p-2.5"
          aria-label="Предыдущий месяц"
        >
          <span className="flex size-5 items-center justify-center">
            <ArrowIcon />
          </span>
        </button>
        <div className="flex-1 text-center text-base font-medium leading-6 text-[#151515]">
          {capitalize(formatDateRu(viewDate, 'LLLL yyyy'))}
        </div>
        <button
          type="button"
          onClick={() => setViewDate(addMonths(viewDate, 1))}
          className="flex items-center gap-2.5 rounded-[100px] bg-[#f1f7fa] p-2.5"
          aria-label="Следующий месяц"
        >
          <span className="flex size-5 rotate-180 items-center justify-center">
            <ArrowIcon />
          </span>
        </button>
      </div>

      <div className="flex w-full flex-col gap-1">
        <div className="flex w-full gap-1">
          {WEEKDAYS.map((day) => (
            <div
              key={day}
              className="flex-1 text-center text-xs font-normal leading-5 text-[#151515] opacity-50"
            >
              {day}
            </div>
          ))}
        </div>
        {weeks.map((week) => (
          <div key={week[0].toISOString()} className="flex w-full gap-1">
            {week.map((day) => {
              const isSelected = Boolean(value && isSameDay(day, value));
              const isCurrentMonth = isSameMonth(day, viewDate);
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => {
                    onSelect(day);
                    onClose();
                  }}
                  aria-label={formatDateRu(day, 'd MMMM yyyy')}
                  aria-current={isSelected ? 'date' : undefined}
                  className={classNames(
                    'flex h-9 flex-1 items-center justify-center rounded-xl text-sm font-normal leading-5 transition',
                    isSelected && 'bg-[#0788f1] font-medium text-white',
                    !isSelected &&
                      isSameDay(day, today) &&
                      'border border-[#0788f1] text-[#151515]',
                    !isSelected &&
                      !isSameDay(day, today) &&
                      'text-[#151515] hover:bg-[#f1f7fa]',
                    !isCurrentMonth && !isSelected && 'opacity-30',
                  )}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );

  if (!isOpen) return null;

  if (!isDesktop) {
    return (
      <BottomSheet isOpen={isOpen} onClose={onClose} title={title}>
        <div className="px-1 pb-2 pt-4">{grid}</div>
      </BottomSheet>
    );
  }

  const rect = anchorRef?.current?.getBoundingClientRect();

  // Портал обязателен: панель модалки анимируется через transform, а он делает
  // её точкой отсчёта для position: fixed — календарь уезжал за пределы экрана.
  return createPortal(
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div
        className="absolute w-80 rounded-3xl bg-white p-5 shadow-[0px_2px_16px_#1515151f]"
        style={{
          top: rect
            ? `${Math.max(
                8,
                Math.min(rect.bottom + 8, window.innerHeight - 380),
              )}px`
            : '20%',
          left: rect
            ? `${Math.max(8, Math.min(rect.left, window.innerWidth - 340))}px`
            : '20%',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        {grid}
      </div>
    </div>,
    document.body,
  );
};
